/**
 * Manager for engine lifecycle and rendering
 */

import type { Scene } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type { EventBus } from "../core/EventBus";
import type { IEngineAdapter } from "./adapters/IEngineAdapter";
import { NullEngineAdapter } from "./adapters/NullEngineAdapter";
import { WebGLEngineAdapter } from "./adapters/WebGLEngineAdapter";
import { WebGPUEngineAdapter } from "./adapters/WebGPUEngineAdapter";
import type {
  AnyEngine,
  EngineManagerConfig,
  EngineOptions,
  EngineType,
} from "./types";

/**
 * Manages engine creation, lifecycle, and rendering
 * Coordinates with adapters for different engine types
 */
export class EngineManager extends BaseManager {
  private adapter: IEngineAdapter | null = null;
  private config: EngineManagerConfig = {};
  private currentScene: Scene | null = null;
  private renderLoopRunning = false;

  /**
   * Create a new EngineManager
   * @param eventBus The event bus for manager communication
   * @param config Engine manager configuration
   */
  constructor(eventBus: EventBus, config?: EngineManagerConfig) {
    super(eventBus);
    this.config = config || {};
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    // Engine creation happens separately via createEngine
    // This just sets up the manager
    this.emit("engineManagerReady", {
      config: this.config,
    });
  }

  /**
   * Create an engine with the specified options
   * @param options Engine creation options
   */
  async createEngine(options: EngineOptions): Promise<AnyEngine> {
    this.ensureInitialized();

    // Dispose previous engine if it exists
    if (this.adapter) {
      const previousType = this.adapter.type;
      this.adapter.dispose();
      this.emit("engineDisposed", { type: previousType as EngineType });
      this.adapter = null;
    }

    // Merge with default options
    const mergedOptions: EngineOptions = {
      ...this.config.defaultOptions,
      ...options,
    };

    // Create appropriate adapter based on engine type
    this.adapter = this.createAdapter(mergedOptions.type);

    // Create the engine through the adapter
    const engine = await this.adapter.createEngine(mergedOptions);

    // Set up auto-resize if configured
    if (this.config.autoResize && typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }

    // Emit engine created event
    this.emit("engineCreated", {
      engine,
      type: mergedOptions.type,
    });

    return engine;
  }

  /**
   * Create the appropriate adapter for the engine type
   * @param type The engine type
   */
  private createAdapter(type: EngineType): IEngineAdapter {
    switch (type) {
      case "null":
        return new NullEngineAdapter();
      case "webgl":
        return new WebGLEngineAdapter();
      case "webgpu":
        return new WebGPUEngineAdapter();
      default:
        throw new Error(`Unsupported engine type: ${type}`);
    }
  }

  /**
   * Get the current engine instance
   */
  getEngine(): AnyEngine | null {
    this.ensureInitialized();
    return this.adapter?.engine || null;
  }

  /**
   * Get the rendering canvas
   */
  getCanvas(): HTMLCanvasElement | null {
    return this.adapter?.getCanvas() || null;
  }

  /**
   * Set the current scene for rendering
   * @param scene The scene to render
   */
  setScene(scene: Scene | null): void {
    this.currentScene = scene;
  }

  /**
   * Start the render loop with a custom callback
   * @param callback The render callback function
   */
  startRenderLoop(callback?: () => void): void {
    this.ensureInitialized();

    if (!this.adapter) {
      throw new Error("No engine created");
    }

    if (this.renderLoopRunning) {
      return; // Already running
    }

    const renderFunction =
      callback ||
      (() => {
        if (this.currentScene) {
          this.currentScene.render();
        }
      });

    this.adapter.startRenderLoop(renderFunction);
    this.renderLoopRunning = true;

    this.emit("renderLoopStarted", {
      engine: this.adapter.engine,
    });
  }

  /**
   * Stop the render loop
   */
  stopRenderLoop(): void {
    if (!this.adapter || !this.renderLoopRunning) {
      return;
    }

    this.adapter.stopRenderLoop();
    this.renderLoopRunning = false;

    this.emit("renderLoopStopped", {
      engine: this.adapter.engine,
    });
  }

  /**
   * Handle window resize events
   */
  private handleResize = (): void => {
    if (!this.adapter) {
      return;
    }

    this.adapter.resize();

    const canvas = this.adapter.getCanvas();
    if (canvas) {
      this.emit("engineResized", {
        height: canvas.height,
        width: canvas.width,
      });
    }
  };

  /**
   * Manually resize the engine
   * @param width New width
   * @param height New height
   */
  resize(width?: number, height?: number): void {
    if (!this.adapter) {
      throw new Error("No engine created");
    }

    this.adapter.resize(width, height);

    if (width !== undefined && height !== undefined) {
      this.emit("engineResized", {
        height,
        width,
      });
    }
  }

  /**
   * Get current frames per second
   */
  getFps(): number {
    return this.adapter?.getFps() || 0;
  }

  /**
   * Get delta time between frames
   */
  getDeltaTime(): number {
    return this.adapter?.getDeltaTime() || 0;
  }

  /**
   * Check if the engine supports a feature
   * @param feature Feature name to check
   */
  supportsFeature(feature: string): boolean {
    return this.adapter?.supportsFeature(feature) || false;
  }

  /**
   * Get the adapter type
   */
  getAdapterType(): string | null {
    return this.adapter?.type || null;
  }

  /**
   * Check if an engine is created
   */
  hasEngine(): boolean {
    return this.adapter !== null && this.adapter.engine !== null;
  }

  /**
   * Check if the render loop is running
   */
  isRenderLoopRunning(): boolean {
    return this.renderLoopRunning;
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    // Stop render loop if running
    if (this.renderLoopRunning) {
      this.stopRenderLoop();
    }

    // Remove resize listener
    if (this.config.autoResize && typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }

    // Emit disposal event before disposing adapter
    if (this.adapter) {
      this.emit("engineDisposed", {
        type: this.adapter.type as EngineType,
      });
    }

    // Dispose of the adapter
    if (this.adapter) {
      this.adapter.dispose();
      this.adapter = null;
    }

    this.currentScene = null;
  }
}
